import React from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { saleOperationValues } from '../../util/saleOperations';

import { SECTOR_LABELS, FACTURACION_LABELS, MODALIDAD_LABELS } from '../../util/listingFieldLabels';

import css from './ListingFieldsGrid.module.css';

// LISTING-FIELDS-GRID — bloque de 6 campos label/valor, ÚNICA fuente del
// patrón usado por UroboroxListingCard y por la summary card de
// TransactionPage. Centraliza el formato y los copys (empleados, antigüedad)
// para que ambos sean idénticos.
//
// `columns` controla el grid en desktop (≥640px): 3 para las cards generales,
// 2 para la summary card del aside (más estrecha, ~409px). En móvil siempre
// apilado (label izquierda / valor derecha). El breakpoint 640px se hereda de
// UroboroxListingCard para no alterar su layout al centralizar el patrón.

// Copys cortos de antigüedad (columna "FUNDADA HACE"), pensados para que el
// valor quepa en una línea. configListing.js mantiene su propia versión
// ("< 2 años", etc.) para los filtros y el formulario de edición.
const ANTIGUEDAD_COPY = {
  'menos-2': 'Menos de 2 años',
  '2-5': '2-5 años',
  '5-10': '5-10 años',
  '10-20': '10-20 años',
  'mas-20': 'Más de 20 años',
};

const empleadosRange = n => {
  if (n == null || Number.isNaN(n)) return '—';
  if (n <= 5) return '1-5';
  if (n <= 15) return '6-15';
  if (n <= 50) return '16-50';
  if (n <= 100) return '51-100';
  return '+100';
};

const Cell = ({ label, value, clamp, className }) => (
  <div className={classNames(css.cell, className)}>
    <span className={css.cellLabel}>{label}</span>
    <span className={classNames(css.cellValue, { [css.cellValueClamp]: clamp })}>{value}</span>
  </div>
);

// Sale-card summaries only; full labels remain in the listing detail and editor.
const OPERATION_LABELS = {
  'venta-total': 'ListingFieldsGrid.operation.sale',
  'inversor-minoritario': 'ListingFieldsGrid.operation.minority',
  'inversor-mayoritario': 'ListingFieldsGrid.operation.majority',
};

const ListingFieldsGrid = ({ publicData = {}, columns = 3, className }) => {
  const intl = useIntl();
  const sectorLabel = SECTOR_LABELS[publicData.sector] || publicData.sector || '—';
  const modalidadLabel = MODALIDAD_LABELS[publicData.modalidad] || '—';
  // FASE2-SCHEMA-01.4: `tipo_operacion` pasó de valor único a lista. Se
  // aceptan las dos formas porque los anuncios anteriores a la migración
  // siguen guardando un string suelto.
  const tiposOperacion = saleOperationValues(publicData.tipo_operacion);
  const operationLabels = [...new Set(tiposOperacion)]
    .map(type => OPERATION_LABELS[type])
    .filter(key => typeof key === 'string')
    .map(id => intl.formatMessage({ id }));
  const tipoOperacionLabel =
    operationLabels.length > 1
      ? intl.formatMessage(
          { id: 'ListingFieldsGrid.moreOperations' },
          { first: operationLabels[0], count: operationLabels.length - 1 }
        )
      : operationLabels[0] || '—';
  const facturacionLabel = FACTURACION_LABELS[publicData.facturacion] || '—';
  const empleadosLabel = empleadosRange(publicData.empleados);
  const antiguedadLabel = ANTIGUEDAD_COPY[publicData.antiguedad] || '—';

  return (
    <div className={classNames(css.dataBlock, columns === 2 ? css.cols2 : css.cols3, className)}>
      <Cell label="SECTOR" value={sectorLabel} clamp />
      <Cell label="OPERATIVA" value={modalidadLabel} />
      <Cell label="OPERACIÓN" value={tipoOperacionLabel} className={css.operationCell} />
      <Cell label="FACTURACIÓN" value={facturacionLabel} />
      <Cell label="EMPLEADOS" value={empleadosLabel} />
      <Cell label="FUNDADA HACE" value={antiguedadLabel} />
    </div>
  );
};

export default ListingFieldsGrid;
